import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { useForm, useFieldArray, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  InputControlled,
  FileInput,
  SelectControlled,
  Loading,
} from "components";
import { PatientContext } from "contexts/PatientContext";
import { NewPatientStep } from "./NewPatientStep";
import { uploadFile } from "services/files";
import { INTITUTION_TYPES, riskLevel_OPTIONS } from "utils/constants";
import {
  editPatient,
  editPatientLabReports,
  deletePatientReport,
  newPatientLabReports,
} from "services/patients";
import { showError } from "utils/alerts";
import Swal from "sweetalert2";
import { useAuth } from "contexts/AuthContext";
import cn from "classnames";

interface LabReportPageProps {}

interface IFormInputs {
  reports: Array<ReportI>;
  riskLevel: string;
}

export const LabReport: React.FunctionComponent<LabReportPageProps> = () => {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const { user } = useAuth();
  const isBioBancoInstitution =
    user.doctor?.institution.type === INTITUTION_TYPES["BIOBANK"];
  const { isAdmin } = user;
  const navigate = useNavigate();
  const { patient, setPatient, fetching, fetchPatient } =
    useContext<any>(PatientContext);
  const _reports = [
    {
      name: "Immunophenotype",
      content: "",
    },
    {
      name: "Cytogenetics",
      content: "",
    },
    {
      name: "Molecular Studies",
      content: "",
    },
    {
      name: "NGS",
      content: "",
    },
    {
      name: "Medulograma",
      content: "",
    },
  ];
  const defaultReports = isAdmin
    ? [..._reports, { name: "NGS OP", content: "" }]
    : _reports;

  const schema = yup
    .object()
    .shape({
      riskLevel: yup.string(),
      reports: isBioBancoInstitution
        ? yup.array()
        : yup.array().of(
            yup.object().shape({
              name: yup.string().required(t("error.requiredField")),
              content: yup.string(),
            })
          ),
    })
    .required();

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    trigger,
    formState: { errors, dirtyFields, isValid },
  } = useForm<IFormInputs>({
    resolver: yupResolver(schema),
  });

  const {
    fields: reports,
    append,
    update,
    replace,
    remove,
  } = useFieldArray({
    control,
    name: "reports",
    keyName: "key",
  });

  // const newFiles = async (f: any) => {
  // 	try {
  // 		let files = [];
  // 		for await (const file of f) {
  // 			if (file.fileId) {
  // 				files.push({ fileId: file.fileId, id: file.id });
  // 			} else {
  // 				const formData = new FormData();
  // 				formData.append('file', file);
  // 				const { data } = await uploadFile(formData);
  // 				files.push({ fileId: data.id });
  // 			}
  // 		}
  // 		return files;
  // 	} catch (error) {
  // 		throw error;
  // 	}
  // };

  const handleOnSubmit: SubmitHandler<IFormInputs> = async (data) => {
    if (isBioBancoInstitution) return;
    console.log(data);

    try {
      // for await (const r of data.reports) {
      // 	if (r.files && typeof r.files !== 'string') {
      // 		const uploads = await newFiles(r.files);
      // 		if (r.date === null) {
      // 			delete r.date;
      // 		}
      // 		reports.push({ ...r, files: uploads });
      // 	}
      // }
      if (dirtyFields.riskLevel && patientId && !isAdmin) {
        await editPatient(patientId, { riskLevel: data.riskLevel });
      }
      if (patientId && patient?.reports?.length > 0) {
        await editPatientLabReports(patient.id, data.reports);
      } else {
        let reports = data.reports.filter((report, index) => {
          if (report.content != "") {
            return report;
          } else {
            return null;
          }
        });

        await newPatientLabReports({
          patientId: patient.id,
          labReports: [...reports],
        });
      }
      await fetchPatient();
    } catch (error) {
      trigger();
      showError(t("error.savePatientInfo"));
      throw error;
    }
  };

  const addNewReport = () => {
    append({
      name: "",
      // files: undefined,
      date: null,
    });
  };

  const removeReport = (reportIndex: number, reportId: number | string) => {
    if (reportId) {
      Swal.fire({
        title: t("areYouSure"),
        text: t("doYouWantToDeleteReport"),
        footer: t("youCanNotUndoThisAction"),
        icon: "warning",
        showCancelButton: true,
      }).then(async (result: any) => {
        if (result.isConfirmed) {
          try {
            await deletePatientReport(reportId);
            const _reports = reports.splice(reportIndex, 1);
            setPatient({ ...patient, reports: [..._reports] });
            remove(reportIndex);
            await fetchPatient();
          } catch (error) {
            showError(t("error.savePatientInfo"));
          }
        }
      });
    } else {
      remove(reportIndex);
    }
  };

  useEffect(() => {
    if (
      !isBioBancoInstitution &&
      Object.keys(patient).length !== 0 &&
      (patient?.diagnostics?.length === 0 ||
        !patient?.diagnostics[0]?.consent ||
        patient?.diagnostics[0]?.dxSubType?.includes("Other")) &&
      !isAdmin
    ) {
      navigate("../final");
    }
    if (patient?.reports?.length > 0) {
      getExistentValues();
    } else {
      //@ts-ignore
      replace([...defaultReports]);
    }
  }, [patient]);

  const getExistentValues = async () => {
    let _reports = [...defaultReports];
    patient.reports?.forEach((r: any, i: number) => {
      let index = _reports.findIndex((e) => e.name === r.name);
      if (index !== -1) {
        _reports[index] = {
          ...r,
          // files: r.files[0].file.name,
        };
      }
      if (i === patient.reports.length - 1) {
        //@ts-ignore
        replace([..._reports]);
      }
    });
  };

  return (
    <NewPatientStep
      title={`05.- ${t("laboratoryReport")}`}
      prevStep="/treatments"
      nextStep="/final"
      handleOnClickNext={handleSubmit(handleOnSubmit)}
      isValid={isValid}
    >
      <form className="relative block" onSubmit={handleSubmit(handleOnSubmit)}>
        {fetching ? (
          <Loading />
        ) : (
          <>
            <SelectControlled
              name="riskLevel"
              disabled={isAdmin}
              control={control}
              defaultValue={
                patient?.riskLevel
                  ? {
                      value: patient?.riskLevel,
                      label: patient?.riskLevel,
                    }
                  : ""
              }
              className="lg:col-span-2 mb-6"
              labelClass="sm"
              options={riskLevel_OPTIONS}
              label={t("riskLevel")}
              //@ts-ignore
              errors={errors.riskLevel}
            />
            {reports.map((report, i) =>
              !isAdmin && report.name === "NGS OP" ? null : (
                <div
                  className="flex items-center justify-between w-full"
                  key={report.key}
                >
                  <div
                    className={cn(
                      "grid lg:grid-cols-5 grid-cols-1 gap-6 mb-6 items-center w-full"
                    )}
                  >
                    {report?.name === "" && (
                      <InputControlled
                        name={`reports[${i}].name`}
                        control={control}
                        //@ts-ignore
                        errors={errors.name}
                        defaultValue={reports[i].name}
                        className={cn("lg:col-span-1")}
                        labelClass="sm"
                        placeholder={t("reportName")}
                        label={t("reportName")}
                      />
                    )}
                    {/* <FileInput
										name={`reports[${i}].files`}
										control={control}
										//@ts-ignore
										errors={errors?.files}
										defaultValue={
											reports[i].files?.length > 0
												? reports[i]?.files[0].file?.name
												: ''
										}
										label={t(`reports.${reports[i].name}`) || t('file')}
										labelClass='sm'
										className={cn({
											'lg:col-span-5':
												report?.name !== '' &&
												!(report?.date || report?.date === ''),
											'lg:col-span-2 mt-6':
												report?.name === '' &&
												!(report?.date || report?.date === ''),
											'lg:col-span-3': report?.date || report?.date === '',
										})}
									/> */}
                    <InputControlled
                      disabled={isAdmin && report.name !== "NGS OP"}
                      name={`reports[${i}].content`}
                      control={control}
                      className="lg:col-span-6"
                      labelClass="sm"
                      defaultValue={reports[i]?.content}
                      type={
                        reports[i].name === "Medulograma" ? "text" : "textarea"
                      }
                      label={t(reports[i].name)}
                      //@ts-ignore
                      errors={errors.app}
                      onChange={(e: any) => {
                        const newContent = e.target.value;
                        const oldContent = reports[i].content;
                        if (newContent !== oldContent) {
                          update(i, { ...reports[i], content: newContent });
                        }
                      }}
                    />
                    {(report?.date || report?.date === "") && (
                      <InputControlled
                        disabled={isAdmin}
                        name={`reports[${i}].date`}
                        control={control}
                        defaultValue={reports[i].date || ""}
                        //@ts-ignore
                        errors={errors.date}
                        className={cn({
                          "lg:col-span-2": report?.name !== "",
                          "lg:col-span-1": report?.name === "",
                        })}
                        labelClass="sm"
                        type="date"
                        label={t("reportDate")}
                      />
                    )}
                    {/* @ts-ignore */}
                  </div>
                  {/* <Icon className="ml-4" name="Trash" onClick={() => removeReport(i, Number(report.id))} /> */}
                </div>
              )
            )}
          </>
        )}
        {/* <div className="flex justify-start">
                    <Button handleClick={addNewReport}>+ {t('newReport')}</Button>
                </div> */}
      </form>
    </NewPatientStep>
  );
};
